<template>
  <div>
    <div class="comment-form-container">
      <div class="comment-form-header">
        <h5 class="comment-form-title">
          {{ $t('social.write-comment') }}
        </h5>
      </div>
      
      <div class="comment-form-editor">
        <textarea
          ref="commentTextarea"
          v-model="content"
          :placeholder="$t('social.write-comment-placeholder')"
          :disabled="isLoading"
          class="comment-textarea"
          @input="handleEditorChange"
          @keydown.ctrl.enter="handleCommentCreation"
        />
      </div>
      
      <div class="comment-form-actions">
        <button
          type="button"
          class="btn btn-cancel"
          :disabled="isLoading || isEmpty"
          @click="handleCancel"
        >
          {{ $t("action.cancel") }}
        </button>
        <button
          type="button"
          class="btn btn-publish"
          :disabled="isLoading || isEmpty"
          @click="handleCommentCreation"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
          {{ isEditing ? $t('social.edit-comment') : $t("social.post-comment") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CommentForm',
  components: { 
    ToastificationContent 
  },
  props: {
    sourceKey: {
      type: String,
      required: true,
    },
    morphType: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    comment: {
      type: Object,
      default: () => ({}),
    },
    leaf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      content: '',
      isEmpty: true,
      isLoading: false,
    };
  },
  computed: {
    currentCollective() {
      return this.$store.getters.currentCollective;
    },
  },
  created() {
    this.initializeContent();
  },
  methods: {
    initializeContent() {
      if (this.isEditing && this.comment.content) {
        this.content = this.comment.content;
        this.isEmpty = false;
      }
    },
    focus() {
      this.$refs.commentTextarea.focus();
    },
    handleCancel() {
      this.content = '';
      this.$emit('cancel');
    },
    handleEditorChange() {
      this.isEmpty = this.content.trim().length === 0;
    },
    async handleCommentCreation() {
      if (this.isEmpty || this.isLoading) {
        return;
      }

      try {
        this.isLoading = true;
        
        if (this.isEditing) {
          await this.handleEditComment();
        } else {
          await this.handleCreateComment();
        }
      } catch (error) {
        this.handleError();
      } finally {
        this.isLoading = false;
      }
    },
    async handleEditComment() {
      const storedKey = this.leaf
        ? this.sourceKey
        : `comments-${this.morphType}-${this.sourceKey}`;
      const item = await this.$store.dispatch('editItem', {
        noSet: true,
        item: {
          itemType: 'comments',
          storedKey,
          requestConfig: {
            comment: this.content,
            key: this.comment.key,
            morphType: this.morphType,
          },
        },
      });
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('comments.updated-success'),
          icon: 'CheckIcon',
          variant: 'success',
        },
      });

      this.emitUpdateEvent(item);
      this.resetForm();
    },
    async handleCreateComment() {
      console.log('handleCreateComment called with sourceKey:', this.sourceKey);
      const storedKey = this.leaf
        ? this.sourceKey
        : `comments-${this.morphType}-${this.sourceKey}`;
      const item = await this.$store.dispatch('createItem', {
        noSet: true,
        item: {
          itemType: 'comments',
          storedKey,
          requestConfig: {
            comment: this.content,
            key: this.sourceKey,
            morphType: this.morphType,
          },
        },
      });
      
      // Check if comment is pending moderation
      const commentData = item && item.data ? item.data : null;
      const isPendingModeration = commentData && (commentData.moderationStatus === 0 ||
        (commentData.moderationStatus === null && !this.currentCollective.isStaff));

      if (isPendingModeration) {
        // Show message for pending moderation
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('success-message.created-pending-review'),
            icon: 'ClockIcon',
            variant: 'warning',
          },
        });
      } else {
        // Show standard success message for approved or staff-created comments
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('success-message.general-success-create'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
      }

      this.emitCreateEvent(item);
      this.resetForm();
    },
    emitUpdateEvent(item) {
      this.$emit('update');
      this.emitSocketUpdate(item);
    },
    emitCreateEvent(item) {
      this.$emit('created');
      this.emitSocketCreate(item);
    },
    emitSocketUpdate(item) {
      if (this.itemType) {
        this.$socket.emit('updateData', {
          itemType: 'comments',
          storedKey: this.sourceKey,
          key: this.comment.key,
          data: item.data,
          communitySlug: this.$store.getters.currentCollective.slug,
        });
      }
    },
    emitSocketCreate(item) {
      if (this.itemType) {
        this.$socket.emit('newData', {
          itemType: 'comments',
          storedKey: this.sourceKey,
          data: item.data,
          communitySlug: this.$store.getters.currentCollective.slug,
        });
      }
    },
    resetForm() {
      this.content = '';
      this.isEmpty = true;
    },
    handleError() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('error-message.general-error'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-include";

.comment-form-wrapper {
  margin-bottom: 1.5rem;
}

.comment-form-container {
  background: #ffffff;
  border-top: 1px solid #e1e5e9;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
}

.comment-form-header {
  padding: 1.25rem 1.5rem 0.75rem;
  // border-bottom: 1px solid #f0f2f5;
}

.comment-form-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #6f767f;
  letter-spacing: 0.5px;
}

.comment-form-editor {
  padding: 1rem 1.5rem;
}

.comment-textarea {
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #2c3e50;
  background-color: #fafbfc;
  resize: vertical;
  transition: all 0.2s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  &:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: #adb5bd;
    font-style: italic;
  }
}

.comment-form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.25rem;
  background-color: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

.btn {
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-cancel {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  
  &:hover:not(:disabled) {
    background-color: #f8f9fa;
    color: #495057;
    border-color: #adb5bd;
  }
  
  &:active:not(:disabled) {
    background-color: #e9ecef;
  }
}

.btn-publish {
  background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
  color: #2c3e50;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #ffb300 0%, #ffa000 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(255, 193, 7, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(255, 193, 7, 0.3);
  }
}

.spinner-border {
  width: 1rem;
  height: 1rem;
  border-width: 0.125em;
}

// Responsive design
@media (max-width: 768px) {
  .comment-form-container {
    border-radius: 8px;
  }
  
  .comment-form-header {
    padding: 1rem 1.25rem 0.5rem;
  }
  
  .comment-form-title {
    font-size: 1rem;
  }
  
  .comment-form-editor {
    padding: 0.75rem 1.25rem;
  }
  
  .comment-textarea {
    min-height: 100px;
    padding: 0.75rem;
  }
  
  .comment-form-actions {
    padding: 0.75rem 1.25rem 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
